import React from 'react'
import { mergeClasses } from '../../../utils/mergeClasses'

const DEFAULT_CONTENT_CLASS =
    'tw-bg-white dark:tw-bg-neutral-800 tw-text-neutral-700 dark:tw-text-neutral-200 tw-text-xs tw-shadow-md tw-border tw-border-solid tw-border-neutral-200 dark:tw-border-neutral-700'

const TooltipContext = React.createContext({
    open: false,
    setOpen: () => {},
})

const TooltipProvider = ({ children }) => {
    return <>{children}</>
}

const Tooltip = ({ children, open, defaultOpen = false, onOpenChange }) => {
    const [isOpen, setIsOpen] = React.useState(defaultOpen)
    const controlledOpen = open !== undefined ? open : isOpen

    const handleOpenChange = newOpen => {
        if (open === undefined) setIsOpen(newOpen)
        onOpenChange?.(newOpen)
    }

    return (
        <TooltipContext.Provider
            value={{ open: controlledOpen, setOpen: handleOpenChange }}
        >
            <div className="tw-relative tw-inline-block">{children}</div>
        </TooltipContext.Provider>
    )
}

const TooltipTrigger = React.forwardRef(
    ({ onMouseEnter, onMouseLeave, asChild, children, ...props }, ref) => {
        const { setOpen } = React.useContext(TooltipContext)
        const handlers = {
            onMouseEnter: e => {
                setOpen(true)
                onMouseEnter?.(e)
            },
            onMouseLeave: e => {
                setOpen(false)
                onMouseLeave?.(e)
            },
            onFocus: () => setOpen(true),
            onBlur: () => setOpen(false),
        }

        if (asChild && React.isValidElement(children)) {
            return React.cloneElement(children, { ref, ...handlers })
        }

        return (
            <span
                ref={ref}
                data-slot="tooltip-trigger"
                style={{ display: 'inline-flex' }}
                {...handlers}
                {...props}
            >
                {children}
            </span>
        )
    }
)
TooltipTrigger.displayName = 'TooltipTrigger'

const TooltipContent = React.forwardRef(({ className, ...props }, ref) => {
    const { open } = React.useContext(TooltipContext)
    if (!open) return null

    return (
        <div
            ref={ref}
            data-slot="tooltip-content"
            className={mergeClasses(
                'tw-absolute tw-bottom-full tw-left-1/2 tw-z-[1202] tw-mb-2 -tw-translate-x-1/2 tw-inline-flex tw-w-fit tw-max-w-xs tw-items-center tw-gap-1.5 tw-rounded-md tw-px-3 tw-py-1.5 tw-text-xs tw-whitespace-nowrap',
                DEFAULT_CONTENT_CLASS,
                className
            )}
            {...props}
        />
    )
})
TooltipContent.displayName = 'TooltipContent'

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
